import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  InvoiceArrivalFilterRootParamList,
  InvoiceArrivalRootParamList,
} from "../../../../types";
import {
  Button,
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import {
  FilterListRenderContextType,
  GenericFilterListScreen,
  useGenericFilterListScreen,
} from "../../../../components/GenericFilterListScreen";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { InvoiceArrivalFilterValues } from "../../types";
import { InvoiceArrivalFilterScreen } from "../InvoiceArrivalFilterScreen";
import { useTranslation } from "../../../../utilities/i18n";
import { SelectableProductsFilterScreen } from "../../../../components/SelectableProductsFilterScreen";
import {
  SelectableProductsFilterContext,
  SelectableProductsFilterContextType,
} from "../../../../components/SelectableProductsFilterScreen/context";

export interface InvoiceArrivalFilterRootScreenProps
  extends StackScreenProps<
    InvoiceArrivalRootParamList,
    "InvoiceArrivalFilterRootScreen"
  > {}

export const InvoiceArrivalFilterRootScreen =
  ({}: InvoiceArrivalFilterRootScreenProps) => {
    const { t } = useTranslation("invoice_arrival");

    const listScreenContext = useGenericFilterListScreen();

    const RootNavigator =
      createStackHeaderNavigator<InvoiceArrivalFilterRootParamList>();

    const filterContext = useListFilterContext<InvoiceArrivalFilterValues>();

    const selectableProductsContext: SelectableProductsFilterContextType = {
      selectedProductIds: filterContext.getFilterValues("productId") ?? [],
      onClearAllPress: () => filterContext.setFilterValue("productId", []),
      onProductSelect: (productId) => {
        const existingProductIds = filterContext.getFilterValues("productId");
        if (existingProductIds?.includes(productId)) {
          filterContext.setFilterValue(
            "productId",
            existingProductIds.filter((id) => id !== productId)
          );
          return;
        } else {
          filterContext.setFilterValue("productId", [
            ...(filterContext.getFilterValues("productId") ?? []),
            productId,
          ]);
        }
      },
    };

    return (
      <FilterListRenderContextType.Provider value={listScreenContext}>
        <SelectableProductsFilterContext.Provider
          value={selectableProductsContext}
        >
          <RootNavigator.Navigator
            screenOptions={{
              animationEnabled: true,
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          >
            <RootNavigator.Group
              screenOptions={{
                headerShown: true,
                title: "",
                headerLeft: () => <ModalCloseButton type="Close" />,
                headerRight(props) {
                  return (
                    <Button
                      label={t("common:clear_filters")}
                      isAction
                      disabled={
                        filterContext.filterState.activeFiltersCount === 0
                      }
                      onPress={filterContext.clearAll}
                      size="compact"
                    />
                  );
                },
              }}
            >
              <RootNavigator.Screen
                name="InvoiceArrivalFilterScreen"
                component={InvoiceArrivalFilterScreen}
              />
            </RootNavigator.Group>
            <RootNavigator.Group
              screenOptions={{
                headerLeft: () => <ModalCloseButton type="Back" />,
              }}
            >
              <RootNavigator.Screen
                name="GenericFilterListScreen"
                component={GenericFilterListScreen}
              />
              <RootNavigator.Screen
                name="SelectableProductsFilterScreen"
                component={SelectableProductsFilterScreen}
              />
            </RootNavigator.Group>
          </RootNavigator.Navigator>
        </SelectableProductsFilterContext.Provider>
      </FilterListRenderContextType.Provider>
    );
  };
